import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import back from 'asset/img/back.png';
import gold from 'asset/img/gold.svg';
import WkModal from 'components/Modal';
import background from 'asset/img/bk-person.webp';
import white from 'asset/img/white.webp';
import fiveSvg from 'asset/daily/five.svg';
import miaoSvg from 'asset/daily/miao.svg';
import starSvg from 'asset/daily/star.svg';
import shengjiSvg from 'asset/daily/shengji.svg';
import dakaSvg from 'asset/daily/daka.svg';
import gongxiMp3 from 'asset/music/gongxi.MP3';
import daily2Mp3 from 'asset/pet/hurgay/daily2.MP3';
import { getDailyTaskList, dailyTaskAccept, dailyTaskClaim } from 'network';
import Loadding from 'components/Loadding';
import './index.scss';

const TASK_IMAGES = {
    three_star: starSvg,
    ninety_second: miaoSvg,
    five_today: fiveSvg,
    wukong_upgrade: shengjiSvg,
    daily_checkin: dakaSvg,
};

// 难度指数（颗星）：闯10关3星，75秒闯关3星，3颗星任务2星，打卡/升级1星
const TASK_DIFFICULTY_STARS = {
    five_today: 3,
    ninety_second: 3,
    three_star: 2,
    daily_checkin: 1,
    wukong_upgrade: 1,
};

const getTaskImage = (task) => task.image || TASK_IMAGES[task.task_key];
const getTaskStars = (task) => TASK_DIFFICULTY_STARS[task.task_key] ?? 1;

const DailyTask = () => {
    const navigate = useNavigate();
    const isMounted = useRef(true);
    const [list, setList] = useState([]);
    const [todayClaimedCount, setTodayClaimedCount] = useState(0);
    const [totalClaimedCount, setTotalClaimedCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [acceptingKey, setAcceptingKey] = useState(null);
    const [claimingKey, setClaimingKey] = useState(null);
    const [rewardModalVisible, setRewardModalVisible] = useState(false);
    const [rewardCoin, setRewardCoin] = useState(0);

    const fetchList = useCallback(() => {
        getDailyTaskList({}).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0 && res?.data) {
                if (Array.isArray(res.data.list)) setList(res.data.list);
                if (typeof res.data.today_claimed_count === 'number') setTodayClaimedCount(res.data.today_claimed_count);
                if (typeof res.data.total_claimed_count === 'number') setTotalClaimedCount(res.data.total_claimed_count);
            } else {
                message.error(res?.msg || '获取任务列表失败');
            }
        }).catch(() => message.error('获取任务列表失败')).finally(() => {
            if (isMounted.current) setLoading(false);
        });
    }, []);

    useEffect(() => {
        isMounted.current = true;
        setLoading(true);
        fetchList();
        return () => { isMounted.current = false; };
    }, [fetchList]);

    const handleBack = () => navigate(-1);

    // 接任务：点击黄色「领取」，当日任务变为已接（显示未完成）
    const handleAccept = (taskKey) => {
        if (acceptingKey) return;
        setAcceptingKey(taskKey);
        dailyTaskAccept({ task_key: taskKey }).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0) {
                message.success('已领取任务');
                try {
                    if (!localStorage.getItem('pet_first_task_claimed')) localStorage.setItem('pet_first_task_claimed', '1');
                } catch (e) {}
                fetchList();
            } else {
                message.error(res?.msg || '领取失败');
            }
        }).catch(() => message.error('领取失败')).finally(() => {
            if (isMounted.current) setAcceptingKey(null);
        });
    };

    // 领取奖励：达成条件后点击黄色「领取奖励」，发金币并弹窗
    const handleClaim = (taskKey) => {
        if (claimingKey) return;
        setClaimingKey(taskKey);
        dailyTaskClaim({ task_key: taskKey }).then((res) => {
            if (!isMounted.current) return;
            if (res?.code === 0) {
                const coin = res?.data?.coin ?? 0;
                setRewardCoin(coin);
                setRewardModalVisible(true);
                fetchList();
                // 移动端复用同一 Audio 会导致第二次起无声，改为每次播放新建实例；最后一个任务用 daily2 鼓励语音
                const unclaimedCount = list.filter((t) => !t.claimed).length;
                const isLastTask = unclaimedCount === 1;
                const src = isLastTask ? daily2Mp3 : gongxiMp3;
                const s = new Audio(src);
                s.play().catch(() => {});
            } else {
                message.error(res?.msg || '领取失败');
            }
        }).catch(() => message.error('领取失败')).finally(() => {
            if (isMounted.current) setClaimingKey(null);
        });
    };

    // 只展示未领取的任务，按难度指数升序（1星→2星→3星）；全部完成后展示「今日已全部完成」区块
    const displayList = useMemo(() => {
        const pending = list.filter((t) => !t.claimed);
        return pending.sort((a, b) => getTaskStars(a) - getTaskStars(b));
    }, [list]);
    const totalCount = list.length;
    const allDone = totalCount > 0 && displayList.length === 0;

    if (loading && list.length === 0) {
        return (
            <div className="daily-task-page">
                <img src={background} className="daily-task-bg" alt="" />
                <img src={white} className="daily-task-bg daily-task-bg-white" alt="" />
                <Loadding visible />
                <div className="daily-task-back-wrap" onClick={handleBack}>
                    <img src={back} alt="返回" />
                </div>
            </div>
        );
    }

    return (
        <div className="daily-task-page">
            <img src={background} className="daily-task-bg" alt="" />
            <img src={white} className="daily-task-bg daily-task-bg-white" alt="" />
            <div className="daily-task-back-wrap" onClick={handleBack}>
                <img src={back} alt="返回" />
            </div>
            <div className="daily-task-header">
                <h1>每日任务</h1>
                <div className="daily-task-stats">
                    <div className="daily-task-stat daily-task-stat-today">
                        <span className="daily-task-stat-emoji">📅</span>
                        <span className="daily-task-stat-text">今日完成</span>
                        <strong>{todayClaimedCount}/{totalCount || 3}</strong>
                    </div>
                    <div className="daily-task-stat daily-task-stat-total">
                        <span className="daily-task-stat-emoji">🏆</span>
                        <span className="daily-task-stat-text">累计完成</span>
                        <strong>{totalClaimedCount}</strong>
                        <span className="daily-task-stat-suffix">个任务</span>
                    </div>
                </div>
            </div>
            <WkModal
                visible={rewardModalVisible}
                onOk={() => setRewardModalVisible(false)}
                onCancel={() => setRewardModalVisible(false)}
                okText="太棒啦"
                cancelVisble={false}
                title="领取成功 🎉"
                tongquStyle
                content={
                    <div className="daily-task-reward-content">
                        <div className="daily-task-reward-coin-wrap">
                            <img src={gold} alt="" className="daily-task-reward-coin-icon" />
                            <span className="daily-task-reward-coin-num">+{rewardCoin}</span>
                            <span className="daily-task-reward-coin-label">金币</span>
                        </div>
                        <p className="daily-task-reward-tip">金币已放入你的小钱袋啦～</p>
                    </div>
                }
            />
            <div className="daily-task-list">
                {allDone ? (
                    <div className="daily-task-all-done">
                        <div className="daily-task-all-done-emoji">🌟</div>
                        <div className="daily-task-all-done-title">非常优秀</div>
                        <div className="daily-task-all-done-desc">今日的任务已全部完成</div>
                        <div className="daily-task-all-done-sub">明天再来和悟空一起加油吧～</div>
                    </div>
                ) : (
                    displayList.map((task) => {
                        const imageSrc = getTaskImage(task);
                        return (
                            <div key={task.task_key} className="daily-task-item">
                                <div className="daily-task-item-left">
                                    <div className="daily-task-item-image">
                                        {imageSrc ? <img src={imageSrc} alt="" /> : <span className="daily-task-item-image-placeholder" />}
                                    </div>
                                    <div className="daily-task-item-text">
                                        <div className="daily-task-item-name">{task.name}</div>
                                        <div className="daily-task-item-desc">{task.desc}</div>
                                        <div className="daily-task-item-difficulty">
                                            <span className="daily-task-item-difficulty-label">✨ 难度</span>
                                            <span className="daily-task-item-difficulty-stars" aria-hidden>
                                                <span className="stars-filled">{'★'.repeat(getTaskStars(task))}</span>
                                                <span className="stars-empty">{'☆'.repeat(3 - getTaskStars(task))}</span>
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="daily-task-item-right">
                                    {task.accepted ? (
                                        task.completed ? (
                                            <button
                                                type="button"
                                                className="daily-task-btn daily-task-btn-claim"
                                                onClick={() => handleClaim(task.task_key)}
                                                disabled={claimingKey === task.task_key}
                                            >
                                                {claimingKey === task.task_key ? '领取中...' : '领取奖励'}
                                            </button>
                                        ) : (
                                            <span className="daily-task-btn daily-task-btn-disabled">
                                                {task.task_key === 'five_today' && typeof task.today_study_count === 'number'
                                                    ? `未完成（还差${Math.max(0, 10 - task.today_study_count)}关）`
                                                    : task.task_key === 'ninety_second' && typeof task.today_best_sec === 'number' && task.today_best_sec >= 0
                                                        ? `未完成（当前${task.today_best_sec}秒）`
                                                        : '未完成'}
                                            </span>
                                        )
                                    ) : (
                                        <button
                                            type="button"
                                            className="daily-task-btn daily-task-btn-accept"
                                            onClick={() => handleAccept(task.task_key)}
                                            disabled={acceptingKey === task.task_key}
                                        >
                                            {acceptingKey === task.task_key ? '领取中...' : '领取任务'}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default DailyTask;
